/**
 * Auth store
 * @format
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { User, Session } from '@supabase/supabase-js';

/**
 * Interface for the authenticated user object.
 * This can be shared between the store and hooks.
 */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  provider: string;
}

/**
 * Interface for the Auth store state and actions.
 */
interface AuthState {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  profileCreationInProgress: Set<string>;
  setUser: (user: AuthUser | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  init: () => () => void; // Returns the unsubscribe function for cleanup
  signInWithEmail: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signUpWithEmail: (email: string, password: string, name: string) => Promise<{ data: any; error: any }>;
  signInWithProvider: (provider: 'google' | 'apple') => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
  createOrUpdateProfile: (user: User) => Promise<void>;
}

/**
 * Creates and exports the Zustand store for authentication.
 */
let authListenerUnsub: (() => void) | null = null
let authInitDone = false
const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  session: null,
  loading: true,
  error: null,
  profileCreationInProgress: new Set(),

  // --- SETTERS ---
  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  signOut: async () => {
    await supabase.auth.signOut();
    set({ user: null, loading: false }); // ✅ reset properly
  },

  createOrUpdateProfile: async (user: User) => {
    const { profileCreationInProgress } = get();
    if (profileCreationInProgress.has(user.id)) {
      // console.log('[AuthStore] Profile creation already in progress for user:', user.id);
      return;
    }
    // Add user to the in-progress set to prevent race conditions
    set(state => ({ profileCreationInProgress: new Set(state.profileCreationInProgress).add(user.id) }));

    try {
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') { // PGRST116: row not found
        // console.error('[AuthStore] Error checking existing profile:', profileError);
        return;
      }

      if (!existingProfile) {
        // console.log('[AuthStore] No existing profile. Creating new one for user:', user.id);
        const profileData = {
          id: user.id,
          email: user.email || '',
          name: user.user_metadata?.name || user.email || '',
          role: 'user',
          logs : []
        };
        const { error: insertProfileError } = await supabase.from('profiles').insert(profileData);
      }
    } catch (error) {
      // console.error('[AuthStore] Unexpected error in createOrUpdateProfile:', error);
    } finally {
      // Always remove user from the in-progress set
      set(state => {
        const newSet = new Set(state.profileCreationInProgress);
        newSet.delete(user.id);
        return { profileCreationInProgress: newSet };
      });
    }
  },

  /**
   * Initializes the auth state by checking the current session and setting up
   * a listener for auth state changes.
   */
  // auth.store.ts (top-level module scope)

init: () => {
  if (authInitDone && authListenerUnsub) {
    return authListenerUnsub;
  }

  const { createOrUpdateProfile } = get();
  set({ loading: true });

  supabase.auth.getSession()
    .then(({ data: { session } }) => {
      // console.log(`Session is here ${session}`)
      if (session?.user) {
        const authUser: AuthUser = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name || session.user.email || '',
          provider: session.user.app_metadata?.provider || 'email',
        };
        set({ user: authUser, session: session as Session, loading: false, error: null });
        createOrUpdateProfile(session.user);
      } else {
        set({ user: null, session: null, loading: false, error: null });
      }
    })
    .catch((err) => {
      // console.error('[AuthStore] getSession error:', err);
      set({ user: null, session: null, loading: false, error: err?.message || null });
    });

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      // console.log('Auth state change:', event, 'Session exists:', !!session);
      
      if (session?.user) {
        const authUser: AuthUser = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name || session.user.email || '',
          provider: session.user.app_metadata?.provider || 'email',
        };
        
        // console.log('Setting user and loading: false');
        set({ user: authUser, session: session as Session, loading: false, error: null });
        
        if (event === 'SIGNED_IN') {
          setTimeout(() => {
            createOrUpdateProfile(session.user);
          }, 0);
        }
      } else {
        // console.log('No session, setting loading: false');
        set({ user: null, session: null, loading: false, error: null });
      }
    });
    
    

  authListenerUnsub = () => {
    try {
      listener.subscription.unsubscribe();
    } finally {
      authInitDone = false;
      authListenerUnsub = null;
    }
  };

  authInitDone = true;
  return authListenerUnsub;
},

  

  /**
 * Signs in a user with their email and password.
 */
signInWithEmail: async (email, password) => {
    set({ error: null });
    try {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
            set({ error: error.message });
        }
        return { data, error };
    } catch (err) {
        set({ error: 'Connection error' });
        return { data: null, error: err };
    }
},


  /**
   * Signs up a new user.
   */
  signUpWithEmail: async (email, password, name) => {
    set({ error: null });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    if (error) {
      set({ error: error.message });
    }
    // onAuthStateChange will handle profile creation and setting user/session state
    return { data, error };
  },

  /**
   * Signs in a user with an OAuth provider (Google or Apple).
   */
  signInWithProvider: async (provider) => {
    set({ loading: true, error: null });
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    
    if (error) {
      set({ error: error.message, loading: false });
    }
    // For OAuth, we should set loading to false as the redirect will happen
    if (!error) {
      set({ loading: false });
    }
    
    return { data, error };
  },
}));

export { useAuthStore };
